import React, {
    Context,
    ReactNode,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useMachine, useService } from '@xstate/react';
import { State, StateSchema } from 'xstate';
import { useHistory } from 'react-router-dom';

import { queryStrings } from '@trqk/resolvers';
import {
    constants,
    getWebpackEnvVar,
    updateAndReplaceURLSearchParams,
} from '@trqk/utils';

import { entitySelectionMachine } from '.';
import { redirectOnMissingToken } from '../../utils';
import { URL_PARAMS } from '../../constants';

const { GET_ACTIVE_ENTITIES, GET_SESSION } = queryStrings;

interface EntityProviderProps {
    children: ReactNode;
    EntityContext: Context<EntityProviderValue>;
}

/**
 * Exposes an object with properties and methods for entity selection. Entity selection is global;
 * this provider controls entity selection across the app.
 * @returns {EntityProviderValue}
 */
export const EntityProvider = ({
    children,
    EntityContext,
}: EntityProviderProps): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const sessionQuery = useQuery(GET_SESSION);
    const userId = sessionQuery?.data?.session?.currentUser?.id ?? null;
    const machine = useMachine<EntitySelectionContext, EntitySelectionEvent>(
        entitySelectionMachine,
    );
    const send = machine[1];
    const service = machine[2];

    // stateListenerRef contains EntityStateListeners, an object whose keys are strings and values
    // are listener functions. Each listener function will be called with the state of the
    // entitySelectorMachine when it transitions.
    const stateListenerRef = useRef<EntityStateListeners>({});

    useEffect(() => {
        service.onTransition(
            (
                state: State<
                    EntitySelectionContext,
                    EntitySelectionEvent,
                    StateSchema<EntitySelectionContext>,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    any
                >,
            ): void => {
                if (stateListenerRef.current) {
                    Object.values(stateListenerRef.current).forEach(
                        (listener: EntityStateListener): void => {
                            listener(state);
                        },
                    );
                }
            },
        );
    }, [service, stateListenerRef]);

    // This callback allows us to set the state of the EntityStateListeners object on
    // stateListenerRef.current
    const setStateListeners = useCallback(
        (setState: SetStateOfEntityListeners): void => {
            if (stateListenerRef.current) {
                stateListenerRef.current = setState(stateListenerRef.current);
            }
        },
        [],
    );

    const [state] = useService(service);
    const {
        context: {
            activeEntities,
            disableSelection,
            entities,
            modifiedAt,
            priorSelectedEntityId,
            selectedEntity,
            selectedEntityId,
            viewAll,
        },
    } = state;

    const history = useHistory();

    /**
     * Keep URL param for `entity_id` synced to the currently selected entity
     */
    const onSelectEntity = useCallback(
        (entityId: Maybe<string>) => {
            updateAndReplaceURLSearchParams(history, [
                {
                    key: URL_PARAMS.ENTITY_ID,
                    value: entityId,
                },
            ]);
        },
        [history],
    );

    const [getActiveEntities, getActiveEntitiesQuery] = useLazyQuery(
        GET_ACTIVE_ENTITIES,
        {
            fetchPolicy: 'network-only',
            onCompleted: data => {
                const nextActiveEntities =
                    data?.getActiveEntities?.activeEntities ?? null;

                if (nextActiveEntities !== null) {
                    const nextEntities = nextActiveEntities.map(
                        ({ entity }: ActiveEntity): TrqkEntity => entity,
                    );

                    const searchParams = new URLSearchParams(
                        history.location.search,
                    );

                    send({
                        type: 'SET_ENTITY_DATA',
                        value: {
                            activeEntities: nextActiveEntities,
                            entities: nextEntities,
                            onSelectEntity,
                            // Set the selected entity from the `entity_id` URL param, if it exists
                            selectedEntityId: searchParams.get(
                                URL_PARAMS.ENTITY_ID,
                            ),
                        },
                    });
                }
            },
            onError: (error: Error): void => {
                if (
                    error.message ===
                    constants.CLIENT_ERROR_MESSAGES.TOKEN_MISSING
                ) {
                    redirectOnMissingToken();
                }
            },
        },
    );

    useEffect(() => {
        // When userId is available, fetch the active entities
        if (userId) {
            getActiveEntities({
                variables: {
                    clientId,
                    userPoolId,
                    trqkDirectoryUrl,
                },
            });
        }
    }, [userId, getActiveEntities, send]);

    const loading =
        sessionQuery.loading ||
        !getActiveEntitiesQuery.called ||
        getActiveEntitiesQuery.loading;

    const noSelectedEntity =
        sessionQuery.called &&
        getActiveEntitiesQuery.called &&
        !sessionQuery.loading &&
        !getActiveEntitiesQuery.loading &&
        selectedEntity === null;

    return (
        <EntityContext.Provider
            value={{
                activeEntities,
                disableSelection,
                entities,
                loading,
                modifiedAt,
                noSelectedEntity,
                onSelectEntity,
                priorSelectedEntityId,
                sendEntitySelectionEvent: send,
                selectedEntity,
                selectedEntityId,
                setStateListeners,
                userId,
                viewAll,
            }}
        >
            {children}
        </EntityContext.Provider>
    );
};
